import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const activeCat = params.get("category") || "All";
  const activeType = params.get("type");

  const TYPE_LABELS = { shirts: "Shirts & hoodies", jackets: "Jackets", pants: "Pants & shorts" };
  const TYPE_KEYWORDS = {
    shirts: ["shirt", "jersey", "hoodie", "tee", "flannel", "top"],
    jackets: ["jacket", "coat", "vest"],
    pants: ["pants", "pant", "trouser", "shorts", "legging"],
  };
  const shown = activeType
    ? products.filter((p) => (TYPE_KEYWORDS[activeType] || []).some((k) => p.name.toLowerCase().includes(k)))
    : products;

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(["All", ...r.data]));
  }, []);

  useEffect(() => {
    const p = {};
    if (activeCat !== "All") p.category = activeCat;
    if (search) p.search = search;
    setLoading(true);
    const fetchProducts = (attempt = 0) =>
      api.get("/products", { params: p })
        .then((r) => { setProducts(r.data); setLoading(false); })
        .catch(() => {
          if (attempt < 3) setTimeout(() => fetchProducts(attempt + 1), 1500);
          else setLoading(false);
        });
    fetchProducts();
  }, [activeCat, search]);

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-8">
        {activeType ? TYPE_LABELS[activeType] || "Shop all" : activeCat !== "All" ? activeCat : "Shop all"}
      </h1>

      {activeType && (
        <button
          onClick={() => setParams({})}
          className="inline-flex items-center gap-2 mb-6 px-4 py-1.5 bg-ink text-white text-xs font-bold uppercase tracking-wide rounded-full hover:bg-brand transition-colors"
          data-testid="clear-type-filter"
        >
          {TYPE_LABELS[activeType]} ✕
        </button>
      )}

      <div className="flex flex-col md:flex-row md:items-center gap-4 mb-10">
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c.toLowerCase()}`}
              onClick={() => setParams(c === "All" ? {} : { category: c })}
              className={`px-5 py-2 text-sm font-bold uppercase tracking-wide border transition-colors ${
                activeCat === c ? "bg-ink text-white border-ink" : "border-ink/20 hover:border-ink"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="md:ml-auto relative">
          <Search size={18} className="absolute left-0 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="shop-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products…"
            className="pl-7 pr-2 py-2 border-b border-ink/30 focus:border-brand outline-none bg-transparent text-sm w-full md:w-64"
          />
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="shop-loading">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="bg-ink/5 aspect-square rounded-md mb-3" />
              <div className="bg-ink/5 h-4 w-3/4 rounded mb-2" />
              <div className="bg-ink/5 h-4 w-1/3 rounded" />
            </div>
          ))}
        </div>
      ) : shown.length === 0 ? (
        <p className="text-muted-foreground py-20 text-center" data-testid="shop-empty">No products found.</p>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="shop-grid">
          {shown.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}
